<script>
</script>

<svelte:head>
	<title>Privacy Policy - OS Studio</title>
	<meta name="description" content="OS Studio privacy policy. Learn how we handle and protect your personal data." />
</svelte:head>

<div class="min-h-screen bg-white">
	<!-- Header -->
	<div class="bg-gradient-to-br from-purple-900 to-blue-900 text-white py-16">
		<div class="container mx-auto px-4 max-w-4xl">
			<h1 class="text-5xl font-bold mb-4">Privacy Policy</h1>
			<p class="text-xl text-purple-100">Last Updated: February 2026</p>
		</div>
	</div>

	<!-- Content -->
	<div class="container mx-auto px-4 max-w-4xl py-16">
		<div class="prose prose-lg max-w-none">
			<h2>Introduction</h2>
			<p>
				OS Studio ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, website, and services.
			</p>

			<h2>1. Information We Collect</h2>

			<h3>1.1 Account Information</h3>
			<p>
				When you create an account with OS Studio, we collect:
			</p>
			<ul>
				<li>Name and email address</li>
				<li>Company name and industry</li>
				<li>Contact information</li>
				<li>Authentication credentials (password hash only)</li>
				<li>Billing information (payment method details)</li>
				<li>Profile preferences and settings</li>
			</ul>

			<h3>1.2 Infrastructure & Deployment Information</h3>
			<p>
				Depending on your deployment model:
			</p>
			<ul>
				<li><strong>Self-Hosted:</strong> We only receive API calls and platform usage data. You maintain complete control of your infrastructure and data.</li>
				<li><strong>Managed Deployment:</strong> We store operational logs, system metrics, and deployment configurations necessary for monitoring and support.</li>
			</ul>

			<h3>1.3 Usage Data</h3>
			<p>
				We collect information about how you interact with our platform:
			</p>
			<ul>
				<li>Features used and frequency of use</li>
				<li>System specifications and requirements gathering</li>
				<li>Performance metrics and system health data</li>
				<li>Error reports and crash data</li>
				<li>IP address and device information</li>
				<li>Browser type and version</li>
			</ul>

			<h3>1.4 Generated Code & Specifications</h3>
			<p>
				Any code, specifications, or configurations generated by OS Studio remain your exclusive property. We do not use your generated systems for any commercial purposes.
			</p>

			<h2>2. How We Use Your Information</h2>

			<p>We use the information we collect for the following purposes:</p>

			<ul>
				<li><strong>Service Delivery:</strong> To provide, maintain, and improve the OS Studio platform</li>
				<li><strong>Communication:</strong> To send you service updates, security alerts, and support responses</li>
				<li><strong>Security:</strong> To detect, prevent, and address fraud and security incidents</li>
				<li><strong>Compliance:</strong> To comply with legal obligations and enforce our agreements</li>
				<li><strong>Analytics:</strong> To understand usage patterns and improve our services (anonymized data only)</li>
				<li><strong>Support:</strong> To provide customer support and respond to your inquiries</li>
				<li><strong>Billing:</strong> To process payments and manage your subscription</li>
			</ul>

			<h2>3. Data Ownership & Control</h2>

			<h3>3.1 Your Data Ownership</h3>
			<p>
				<strong>You own all data generated by OS Studio, including:</strong>
			</p>
			<ul>
				<li>Generated code and architecture specifications</li>
				<li>Configuration files and infrastructure-as-code templates</li>
				<li>Deployment artifacts and documentation</li>
				<li>Any custom modifications or extensions</li>
			</ul>

			<h3>3.2 Your Infrastructure Ownership</h3>
			<p>
				<strong>Self-Hosted Deployment:</strong> You retain 100% ownership and control of your infrastructure. OS Studio never accesses your systems except through API calls you authorize.
			</p>
			<p>
				<strong>Managed Deployment:</strong> You own the AWS/GCP/Azure account and all resources within it. You have full administrative access. OS Studio credentials are used only for monitoring and maintenance you explicitly authorize.
			</p>

			<h2>4. Data Sharing & Disclosure</h2>

			<p>We do not sell, rent, or share your personal information with third parties except:</p>

			<ul>
				<li><strong>Service Providers:</strong> With vendors who assist in service delivery (cloud providers, payment processors) under strict data processing agreements</li>
				<li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
				<li><strong>Your Consent:</strong> When you explicitly authorize sharing for specific purposes</li>
				<li><strong>Business Transfers:</strong> In the event of acquisition, merger, or sale of assets (with notice and opportunity to opt-out)</li>
			</ul>

			<h2>5. Data Security</h2>

			<h3>5.1 Security Measures</h3>
			<p>
				We implement industry-standard security controls:
			</p>
			<ul>
				<li>End-to-end encryption for data in transit (TLS 1.3+)</li>
				<li>Encryption at rest using AES-256</li>
				<li>Role-based access controls (RBAC)</li>
				<li>Multi-factor authentication (MFA) support</li>
				<li>Regular security audits and penetration testing</li>
				<li>Intrusion detection and prevention systems</li>
				<li>Comprehensive audit logging</li>
			</ul>

			<h3>5.2 No Password Storage</h3>
			<p>
				We never store passwords. All authentication uses industry-standard hashing algorithms (bcrypt with salt).
			</p>

			<h2>6. Data Retention</h2>

			<p>
				We retain your data for as long as necessary to provide services:
			</p>
			<ul>
				<li><strong>Active Accounts:</strong> Data retained while your account is active</li>
				<li><strong>Inactive Accounts:</strong> Data retained for 30 days after account deletion or inactivity</li>
				<li><strong>Backup Data:</strong> Backups retained for 90 days for disaster recovery purposes</li>
				<li><strong>Legal Compliance:</strong> Some data may be retained longer if required by law</li>
			</ul>

			<p>
				You can request immediate data deletion. We will delete your data within 30 days, except where legally required to retain.
			</p>

			<h2>7. Your Rights (GDPR, CCPA)</h2>

			<p>
				Depending on your location, you may have rights to:
			</p>
			<ul>
				<li><strong>Access:</strong> Request a copy of your personal data</li>
				<li><strong>Correction:</strong> Correct inaccurate data</li>
				<li><strong>Deletion:</strong> Request deletion of your data ("right to be forgotten")</li>
				<li><strong>Portability:</strong> Export your data in standard formats</li>
				<li><strong>Objection:</strong> Object to processing for marketing purposes</li>
				<li><strong>Withdrawal:</strong> Withdraw consent at any time</li>
			</ul>

			<p>
				To exercise these rights, contact us at <a href="mailto:privacy@osstudio.com">privacy@osstudio.com</a>.
			</p>

			<h2>8. Cookies & Tracking</h2>

			<p>
				We use minimal cookies for essential functionality:
			</p>
			<ul>
				<li><strong>Session Cookies:</strong> To keep you logged in</li>
				<li><strong>Security Cookies:</strong> To prevent CSRF attacks</li>
				<li><strong>Analytics Cookies:</strong> Privacy-respecting analytics (no cross-site tracking)</li>
			</ul>

			<p>
				You can disable cookies in your browser settings. However, some platform features may not work correctly.
			</p>

			<h2>9. Third-Party Services</h2>

			<p>
				Our platform integrates with third-party services under strict data processing agreements:
			</p>
			<ul>
				<li><strong>Cloud Providers:</strong> AWS, Google Cloud, Microsoft Azure (only when you authorize)</li>
				<li><strong>Payment Processing:</strong> Stripe, PayPal (PCI-DSS compliant)</li>
				<li><strong>Email Services:</strong> SendGrid (transactional emails only)</li>
			</ul>

			<p>
				These services have their own privacy policies. We recommend reviewing them.
			</p>

			<h2>10. International Data Transfers</h2>

			<p>
				If you access OS Studio from outside the United States, your data may be transferred to, stored in, and processed in the United States. By using OS Studio, you consent to such transfers under appropriate safeguards (Standard Contractual Clauses where applicable).
			</p>

			<h2>11. Children's Privacy</h2>

			<p>
				OS Studio is not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware of such collection, we will promptly delete such information.
			</p>

			<h2>12. Changes to This Policy</h2>

			<p>
				We may update this Privacy Policy periodically. We will notify you of material changes via email or a prominent notice on our website. Your continued use of OS Studio constitutes acceptance of updated terms.
			</p>

			<h2>13. Contact Us</h2>

			<p>
				If you have questions or concerns about this Privacy Policy or our privacy practices:
			</p>
			<ul>
				<li><strong>Email:</strong> <a href="mailto:privacy@osstudio.com">privacy@osstudio.com</a></li>
				<li><strong>Mailing Address:</strong> OS Studio Legal Team, Contact via website</li>
				<li><strong>Data Protection Officer:</strong> Available for GDPR inquiries</li>
			</ul>

			<p>
				We will respond to your inquiry within 10 business days.
			</p>

			<h2>14. GDPR Data Processing</h2>

			<p>
				For GDPR purposes, OS Studio acts as a Data Processor. A formal Data Processing Agreement is available for enterprise customers. Contact <a href="mailto:legal@osstudio.com">legal@osstudio.com</a> to execute a DPA.
			</p>

		</div>
	</div>
</div>

<style>
	:global(body) {
		margin: 0;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
		background: white;
		color: #1a1a1a;
	}

	:global(html, body) {
		width: 100%;
	}

	:global(.prose) {
		--prose-body: #374151;
		--prose-headings: #111827;
		--prose-links: #9333ea;
	}

	:global(.prose h2) {
		@apply text-3xl font-bold text-gray-900 mt-12 mb-4;
	}

	:global(.prose h3) {
		@apply text-2xl font-bold text-gray-900 mt-8 mb-3;
	}

	:global(.prose p) {
		@apply text-gray-700 mb-4 leading-relaxed;
	}

	:global(.prose ul) {
		@apply list-disc list-inside mb-4 space-y-2;
	}

	:global(.prose ul li) {
		@apply text-gray-700;
	}

	:global(.prose a) {
		@apply text-purple-600 font-semibold;
	}

	:global(.prose a:hover) {
		@apply text-purple-700;
	}
</style>
